import { isAxiosError } from 'axios';
import { Result, err, ok } from 'neverthrow';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore utils is in the parent dir
import axios from '@utils/axios';

import {
  QuoteConfirmationStatus,
  QuoteTerminatingAction,
  QuoteValueType,
} from 'pages/QuoteView/Quoting/RequestQuickQuote/constants';
import {
  CarrierCostType,
  ProfitType,
} from 'pages/QuoteView/Quoting/RequestQuickQuote/types';
import { Undef } from 'types/UtilityTypes';
import { ApiError } from 'types/api/ApiError';
import { SuggestionStatus } from 'types/suggestions/LoadSuggestions';
import captureException from 'utils/captureException';

export { QuoteConfirmationStatus, QuoteTerminatingAction };

export type QuotedValueTypeApi = 'linehaul' | 'total_sell';

// TODO: Remove marginType from endpoint and use profitType instead
export type SubmitFinalQuoteBody = {
  finalQuotePrice: number;
  finalMargin: number;
  marginType: ProfitType;
  finalCarrierCost: number;
  carrierCostType: CarrierCostType;
  customerExternalTMSId?: string;
  quoteConfirmationStatus: QuoteConfirmationStatus;
  quoteTerminatingAction?: QuoteTerminatingAction;
  quotedValueType?: QuotedValueTypeApi;
};

export function quoteValueTypeToApi(
  v: Undef<QuoteValueType>
): Undef<QuotedValueTypeApi> {
  if (v === QuoteValueType.Linehaul) return 'linehaul';
  if (v === QuoteValueType.TotalSell) return 'total_sell';
  return undefined;
}

export type BuildUserConfirmedQuoteBodyParams = {
  finalQuotePrice: number;
  finalMargin: number;
  marginType: ProfitType;
  finalCarrierCost: number;
  carrierCostType: CarrierCostType;
  quoteTerminatingAction?: QuoteTerminatingAction;
  quotedValueType?: QuotedValueTypeApi;
  customerExternalTMSId?: string;
};

/**
 * Builds the payload for a user_confirmed quote update. Shared by single QQ
 * (useQuoteHandlers) and batch lane (BatchQuoteInputCard) to avoid duplication.
 */
export function buildUserConfirmedQuoteBody(
  params: BuildUserConfirmedQuoteBodyParams
): SubmitFinalQuoteBody {
  const {
    finalQuotePrice,
    finalMargin,
    marginType,
    finalCarrierCost,
    carrierCostType,
    quoteTerminatingAction,
    quotedValueType,
    customerExternalTMSId,
  } = params;
  return {
    finalQuotePrice,
    finalMargin,
    marginType,
    finalCarrierCost,
    carrierCostType,
    ...(customerExternalTMSId !== undefined && { customerExternalTMSId }),
    quoteConfirmationStatus: QuoteConfirmationStatus.UserConfirmed,
    ...(quoteTerminatingAction && { quoteTerminatingAction }),
    ...(quotedValueType && { quotedValueType }),
  };
}

export async function updateQuoteRequestSuggestion(
  suggestionId: number,
  status:
    | SuggestionStatus.InFlight
    | SuggestionStatus.Accepted
    | SuggestionStatus.Rejected,
  finalQuoteData: Undef<SubmitFinalQuoteBody> = undefined,
  selectedQuickQuoteId: Undef<number> = undefined,
  selectedLtlQuickQuoteIds: Undef<number[]> = undefined
): Promise<Result<null, ApiError>> {
  try {
    const response = await axios.patch(
      `/suggestions/${suggestionId}/quote-request`,
      {
        status: status,
        ...(finalQuoteData && { finalQuoteData }),
        ...(selectedQuickQuoteId != null && { selectedQuickQuoteId }),
        ...(selectedLtlQuickQuoteIds !== undefined
          ? { selectedLtlQuickQuoteIds }
          : {}),
      }
    );

    return ok(response.data);
  } catch (error) {
    captureException(error, { functionName: 'updateQuoteRequestSuggestion' });

    if (!isAxiosError(error)) {
      return err({
        message: 'Unable to update quote request.',
      });
    }

    if (error && isAxiosError(error) && error.response?.status === 401) {
      throw error;
    }

    if (error && error.message === 'Extension context invalidated.') {
      throw error;
    }

    return err({
      message:
        error.response?.data.message || 'Unable to update quote request.',
    });
  }
}
